import React from 'react';
import {
  Grid,
  TextField,
  makeStyles,
  Chip,
  MenuItem,
  FormControl,
  Box,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { PageableTable } from 'components';
import { PageableTableHeader } from 'components/PageableTable/components';
import {
  getTableSettingsStorageKey,
  TableSettings,
  useLocalStorageSettings,
  useDebounce,
} from 'hooks';
import { useLocation } from 'react-router-dom';
import { fetchAudit, fetchUsers } from 'lib/Api/Permissions';
import { AuditEntry, User } from 'lib/Model/Permission';

const useStyles = makeStyles((theme) => ({
  searchContainer: {
    marginBottom: theme.spacing(3),
  },
}));

const AuditTable: React.FC = () => {
  const classes = useStyles();
  const { pathname, hash } = useLocation();
  const [tableSettings, setTableSettings] =
    useLocalStorageSettings<TableSettings>(
      getTableSettingsStorageKey(pathname, hash),
      { rowsPerPage: 25 },
    );

  const [auditData, setAuditData] = React.useState<
    { data: AuditEntry[]; total_count: number } | undefined
  >();
  const [auditLoading, setAuditLoading] = React.useState(false);
  const [auditPageNumber, setAuditPageNumber] = React.useState(0);
  const [auditActorFilter, setAuditActorFilter] = React.useState<User | null>(
    null,
  );
  const [auditTargetFilter, setAuditTargetFilter] = React.useState<User | null>(
    null,
  );
  const [auditActionFilter, setAuditActionFilter] = React.useState('');
  const [auditSubjectFilter, setAuditSubjectFilter] = React.useState('');
  const [usersData, setUsersData] = React.useState<User[] | undefined>();
  const [usersLoading, setUsersLoading] = React.useState(false);
  const debouncedAuditSubject = useDebounce(auditSubjectFilter, 400);

  const auditColumns: PageableTableHeader[] = [
    {
      key: 'timestamp',
      label: 'Timestamp',
      sortable: false,
    },
    {
      key: 'actor',
      label: 'Actor',
      sortable: false,
    },
    {
      key: 'target',
      label: 'Target',
      sortable: false,
    },
    {
      key: 'action',
      label: 'Action',
      sortable: false,
    },
    {
      key: 'subject_type',
      label: 'Subject Type',
      sortable: false,
    },
    {
      key: 'subject_slug',
      label: 'Permission',
      sortable: false,
    },
  ];

  const auditRows =
    auditData?.data?.map((entry, index) => ({
      key: index.toString(),
      cells: [
        {
          key: 'timestamp',
          display: new Date(entry.ts).toLocaleString(),
        },
        {
          key: 'actor',
          display: entry.actor_name,
        },
        {
          key: 'target',
          display: entry.target_name,
        },
        {
          key: 'action',
          display: (
            <Chip
              label={entry.action
                .replace('_', ' ')
                .replace(/\b\w/g, (l) => l.toUpperCase())}
              size="small"
              color={
                entry.action.includes('assign') ||
                entry.action.includes('grant')
                  ? 'primary'
                  : 'primary'
              }
            />
          ),
        },
        {
          key: 'subject_type',
          display: entry.subject_type.replace(/\b\w/g, (l) => l.toUpperCase()),
        },
        {
          key: 'subject_slug',
          display: entry.subject_slug,
        },
      ],
    })) ?? [];

  const fetchAuditData = React.useCallback(() => {
    setAuditLoading(true);
    const pageNumberToLoad = auditPageNumber + 1;
    fetchAudit(
      pageNumberToLoad,
      tableSettings.rowsPerPage,
      auditActorFilter?.google_id || undefined,
      auditTargetFilter?.google_id || undefined,
      auditActionFilter || undefined,
      debouncedAuditSubject || undefined,
    ).then((d) => {
      setAuditLoading(false);
      setAuditData(d);
    });
  }, [
    auditPageNumber,
    tableSettings.rowsPerPage,
    auditActorFilter,
    auditTargetFilter,
    auditActionFilter,
    debouncedAuditSubject,
  ]);

  const fetchUsersData = React.useCallback(() => {
    setUsersLoading(true);
    fetchUsers().then((d) => {
      setUsersLoading(false);
      setUsersData(d);
    });
  }, []);

  React.useEffect(() => {
    fetchUsersData();
  }, [fetchUsersData]);

  React.useEffect(() => {
    fetchAuditData();
  }, [fetchAuditData]);

  React.useEffect(() => {
    setAuditPageNumber(0);
  }, [
    auditActorFilter,
    auditTargetFilter,
    auditActionFilter,
    debouncedAuditSubject,
    tableSettings.rowsPerPage,
  ]);

  return (
    <>
      <Box className={classes.searchContainer}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <Autocomplete
                value={auditActorFilter}
                onChange={(e, newValue) => setAuditActorFilter(newValue)}
                options={usersData || []}
                getOptionLabel={(option) => option.name_surname}
                loading={usersLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Actor"
                    placeholder="Filter by actor"
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <Autocomplete
                value={auditTargetFilter}
                onChange={(e, newValue) => setAuditTargetFilter(newValue)}
                options={usersData || []}
                getOptionLabel={(option) => option.name_surname}
                loading={usersLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Target"
                    placeholder="Filter by target"
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              variant="outlined"
              label="Action"
              select
              value={auditActionFilter}
              onChange={(e) => setAuditActionFilter(e.target.value)}
              InputLabelProps={{ shrink: true }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="assign_role">Assign Role</MenuItem>
              <MenuItem value="remove_role">Remove Role</MenuItem>
              <MenuItem value="grant_extra">Grant Extra</MenuItem>
              <MenuItem value="revoke_extra">Revoke Extra</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              variant="outlined"
              label="Subject Slug"
              placeholder="Filter by subject"
              value={auditSubjectFilter}
              onChange={(e) => setAuditSubjectFilter(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </Box>

      <PageableTable
        tableSettings={tableSettings}
        setTableSettings={setTableSettings}
        showSkeletonLoading={auditLoading}
        columns={auditColumns}
        rows={auditRows}
        loading={auditLoading}
        pageNumber={auditPageNumber}
        rowCount={auditData?.total_count ?? 0}
        onChangePage={setAuditPageNumber}
      />
    </>
  );
};

export default AuditTable;
